import { BaseService } from "./base.service";
import {
  VitalsParameter,
  RecordReadingDto,
  UpdateReadingDto,
  UpdateRangeDto,
  VitalsTrackingDto,
  TrackingConfig,
  VitalsReading,
} from "../types/vitals";

export class VitalsTrackingService extends BaseService {
  constructor() {
    super("/vitals");
  }

  async enableTracking(
    memberId: string,
    enabledParameters: VitalsParameter[],
    initialReadings: Partial<Record<VitalsParameter, Record<string, unknown>>>
  ): Promise<VitalsTrackingDto> {
    return this.post<VitalsTrackingDto>(`/tracking/${memberId}`, {
      enabledParameters,
      initialReadings,
    });
  }

  async getTrackingConfig(memberId: string): Promise<TrackingConfig> {
    return this.get<TrackingConfig>(`/tracking/${memberId}`);
  }

  async disableTracking(
    memberId: string,
    parameters: VitalsParameter[]
  ): Promise<VitalsTrackingDto> {
    return this.patch<VitalsTrackingDto>(`/tracking/${memberId}`, {
      parameters,
    });
  }

  async recordReading(
    memberId: string,
    readingDto: RecordReadingDto
  ): Promise<VitalsReading> {
    return this.post<VitalsReading>(
      `/readings/${memberId}/add-reading`,
      readingDto
    );
  }

  async updateReading(
    memberId: string,
    readingId: string,
    updateDto: UpdateReadingDto
  ): Promise<VitalsReading> {
    return this.patch<VitalsReading>(`/readings/${readingId}`, updateDto);
  }

  async deleteReading(memberId: string, readingId: string): Promise<void> {
    return this.delete<void>(`/readings/${readingId}`);
  }

  async updateCustomRange(
    memberId: string,
    rangeDto: UpdateRangeDto
  ): Promise<VitalsTrackingDto> {
    return this.put<VitalsTrackingDto>(`/config/ranges/${memberId}`, rangeDto);
  }

  async getReadings(
    memberId: string,
    parameter?: VitalsParameter
  ): Promise<{
    data: VitalsReading[];
    total: number;
    page: number;
    pageSize: number;
  }> {
    const body: Record<string, unknown> = {};
    if (parameter) body.filters = { parameter };
    return this.post(`/readings/${memberId}/get-readings`, body);
  }

  async downloadVitalsPdf(
    memberId: string,
    parameter: VitalsParameter,
    startDate?: Date,
    endDate?: Date
  ): Promise<Blob> {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();
    return this.get<Blob>(`/pdf/${memberId}/${parameter}`, {
      responseType: "blob",
      params,
    });
  }
}

const vitalsTrackingService = new VitalsTrackingService();
export default vitalsTrackingService;
